// FTEDN — User profile store (Zustand)
import { create } from 'zustand';
import { apiClient } from '../hooks/useApi';

export interface UserProfile {
  id: string;
  displayName: string;
  email?: string | null;
  avatarUrl?: string | null;
  bio?: string | null;
  xp: number;
  level: number;
  tierName: string;
  streak: number;
  reviewCount: number;
  joinedAt?: string;
}

interface UserState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<UserProfile, 'displayName' | 'bio' | 'avatarUrl'>>) => Promise<boolean>;
}

/** Map raw Firestore user doc to our UserProfile shape */
export function mapProfile(raw: Record<string, unknown>): UserProfile {
  const xp = Number(raw.xp ?? raw.totalXP ?? 0);
  return {
    id: String(raw.id ?? ''),
    displayName: String(raw.displayName ?? 'Eeduner'),
    email: raw.email as string | null ?? null,
    avatarUrl: raw.avatarUrl as string | null ?? null,
    bio: raw.bio as string | null ?? null,
    xp,
    level: Math.floor(xp / 500) + 1,
    tierName: getTierName(xp),
    streak: Number(raw.streak ?? 0),
    reviewCount: Number(raw.reviewCount ?? 0),
    joinedAt: raw.joinedAt as string ?? raw.createdAt as string ?? undefined,
  };
}

/** Determine tier name based on XP thresholds */
export function getTierName(xp: number): string {
  if (xp >= 10000) return 'Elder';
  if (xp >= 5000) return 'Sage';
  if (xp >= 2000) return 'Bloom';
  if (xp >= 500) return 'Sprout';
  return 'Seedling';
}

export const useUserStore = create<UserState>((set) => ({
  profile: null,
  loading: false,
  error: null,

  fetchProfile: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/users/me');
      if (res.success && res.data) {
        set({ profile: mapProfile(res.data), loading: false });
      } else {
        set({ error: res.error?.message ?? 'Failed to load profile', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  updateProfile: async (updates) => {
    try {
      const res = await apiClient.patch('/users/me', updates);
      if (res.success && res.data) {
        set({ profile: mapProfile(res.data) });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },
}));
